import { useState, useEffect } from "react";
// import { useTheme } from '../context/ThemeContext';
import {
  bibleVersesApi,
  BibleVerseInput,
  BibleVerseUpdateInput,
  BibleVersesApi,
} from "./bible-verse-api";
import {
  BibleVerse,
  VerseCategory,
  Language,
  getCategoryDisplayName,
  getLanguageDisplayName,
} from "./types";
import { BibleVersEdit } from "./bible-verse-create";
import { toast } from "react-toastify";

const bibleVerseApi = new BibleVersesApi();

export default function BibleVersesList() {
  // const { theme } = useTheme();
  const [verses, setVerses] = useState<BibleVerse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVerse, setSelectedVerse] = useState<BibleVerse | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    VerseCategory | "ALL"
  >("ALL");
  const [selectedLanguage, setSelectedLanguage] = useState<Language | "ALL">(
    "ALL"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState<BibleVerseInput>({
    reference: "",
    language: Language.ENGLISH,
    category: VerseCategory.INSPIRATIONAL,
    description: "",
    author: "",
    verse_text: "",
    notes: "",
  });

  const fetchVerses = async () => {
    try {
      setLoading(true);
      const verses = await bibleVerseApi.getAllVerses();
      console.log(verses);
      setVerses(verses);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerses();
  }, []);

  const handleDeleteVerse = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this verse?")) return;

    try {
      setVerses((prev) => prev.filter((v) => v.id !== id));
      bibleVerseApi.deleteVerse(id);
      fetchVerses();
      toast.success("Your verse has been deleted.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleEditVerse = (verse: BibleVerse) => {
    setSelectedVerse(verse);
    setFormData({
      reference: verse.reference,
      verse_text: verse.verse_text,
      language: verse.language,
      category: verse.category,
      description: verse.description || "",
      author: verse.author || "",
      notes: "",
    });
    setIsEditing(true);
  };

  const handleFormChange = (field: keyof BibleVerseInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getCategoryColor = (category: VerseCategory) => {
    switch (category) {
      case VerseCategory.WEDDING:
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200";
      case VerseCategory.BIRTHDAY:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case VerseCategory.BAPTISM:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case VerseCategory.COMMUNION:
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case VerseCategory.INSPIRATIONAL:
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      default:
        return "bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200";
    }
  };

  const getLanguageColor = (language: Language) => {
    switch (language) {
      case Language.ENGLISH:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case Language.SPANISH:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case Language.FRENCH:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case Language.GERMAN:
        return "bg-black text-white dark:bg-gray-800 dark:text-gray-200";
      case Language.CHINESE:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case Language.HINDI:
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case Language.ARABIC:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      default:
        return "bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200";
    }
  };

  // Filter verses based on selected category, language, and search term
  const filteredVerses = verses.filter((verse) => {
    const matchesCategory =
      selectedCategory === "ALL" || verse.category === selectedCategory;
    const matchesLanguage =
      selectedLanguage === "ALL" || verse.language === selectedLanguage;
    const matchesSearch =
      searchTerm === "" ||
      verse.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      verse.verse_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (verse.description &&
        verse.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCategory && matchesLanguage && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-red-500 dark:text-red-400">{error}</div>
      </div>
    );
  }

  function handleClose(): void {
    setIsCreating(false);
    setIsEditing(false);
    fetchVerses();
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black dark:text-white mb-2">
              Bible Verses
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Manage and organize Bible verses by categories and languages
            </p>
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
          >
            Add Verse
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border dark:border-neutral-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Search verses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) =>
                  setSelectedCategory(e.target.value as VerseCategory | "ALL")
                }
                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
              >
                <option value="ALL">All Categories</option>
                {Object.values(VerseCategory).map((category) => (
                  <option key={category} value={category}>
                    {getCategoryDisplayName(category)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) =>
                  setSelectedLanguage(e.target.value as Language | "ALL")
                }
                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
              >
                <option value="ALL">All Languages</option>
                {Object.values(Language).map((language) => (
                  <option key={language} value={language}>
                    {getLanguageDisplayName(language)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory("ALL");
                  setSelectedLanguage("ALL");
                }}
                className="w-full px-3 py-2 bg-neutral-500 text-white rounded-lg hover:bg-neutral-600"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
          {/* Verses List */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border dark:border-neutral-700">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-black dark:text-white mb-4">
                Verses ({filteredVerses.length})
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {filteredVerses.length === 0 ? (
                  <div className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                    No verses found matching your criteria.
                  </div>
                ) : (
                  filteredVerses.map((verse) => (
                    <div
                      key={verse.id}
                      className="border dark:border-neutral-700 rounded-lg p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-black dark:text-white">
                          {verse.reference}
                        </h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditVerse(verse)}
                            className="text-blue-500 hover:text-blue-600 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteVerse(verse.id)}
                            className="text-red-500 hover:text-red-600 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="mb-2">
                        <p className="text-neutral-600 dark:text-neutral-400 text-sm italic">
                          "{verse.verse_text}"
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${getCategoryColor(
                            verse.category
                          )}`}
                        >
                          {getCategoryDisplayName(verse.category)}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getLanguageColor(
                            verse.language
                          )}`}
                        >
                          {getLanguageDisplayName(verse.language)}
                        </span>
                      </div>
                      {verse.description && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                          {verse.description}
                        </p>
                      )}
                      <div className="text-xs text-neutral-400 dark:text-neutral-500">
                        {verse.author && `Author: ${verse.author} • `}
                        Added: {new Date(verse.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Verse Form */}
          {(isCreating || isEditing) && (
            <BibleVersEdit
              isCreate={isCreating}
              onClose={handleClose}
              verse={selectedVerse}
            />
          )}
        </div>
      </div>
    </div>
  );
}
